import React, {useCallback, useMemo, useState} from 'react';
import {classes, style} from './doppe-viewer-claim-download-form.st.css';
import {
    ButtonType,
    reactCreateMemo,
    TextBoxTypes,
    useAsyncOperation,
    useEventCallback,
} from '@wix/devzai-utils-react';
import {AsyncValueStates, valueConvertWhenNotNullable} from '@wix/devzai-utils-common';
import {useDoppeViewerLocales} from '../../client/doppe-viewer-locales/doppe-viewer-locales';
import {useDoppeViewerClientServices} from '../../client/doppe-user-app/doppe-viewer-client-services-react';
import {bymoPagePropsFindActionById, useBymoPageProps} from '../../client-server-common/bymo-pages/bymo-page-props';
import {
    doppeActionResolveBoundDownloadFileId
} from '../../doppe-action-types/doppe-action-type-download-file/doppe-action-type-download-file-utils';
import {
    DoppeViewerHorizontalButton,
    DoppeViewerHorizontalButtonPriority,
    DoppeViewerHorizontalButtonSkin
} from '../doppe-viewer-horizontal-button/doppe-viewer-horizontal-button';
import {DoppeWixCheckoutOrderMatchStatus} from '../../client-server-common/doppe-wix-checkout-order-validation/doppe-wix-checkout-order-validation';
import {DoppeViewerAutoForm} from '../doppe-viewer-auto-form/doppe-viewer-auto-form';
import {
    doppeViewerTextBoxControlCreate
} from '../doppe-viewer-auto-form/doppe-viewer-form-control-factories/doppe-viewer-text-box-control/doppe-viewer-text-box-control';
import {
    doppeViewerClaimDownloadFormCreateEmailFormatValidator
} from './doppe-viewer-claim-download-form-validators';

type ClaimDownloadFormData = {
    orderNumber: string;
    email: string;
};

/**
 * Shared "Already purchased? Enter your order details" form used by both the
 * post-checkout `DoppeViewerClaimDownloadModal` and the pre-checkout
 * `DoppeViewerBuyOrClaimDownloadModal`. The form resolves the action's
 * `mediaFileId`, collects order number + email through the standard
 * `DoppeViewerAutoForm` primitives, calls `generatePaidDownloadFileUrl`, and
 * navigates to the signed download URL on a successful match. A parent can opt
 * in to a secondary "Close" button by passing `onCancel`; the new Buy/Claim
 * modal omits it (its `X` is enough).
 */
export const DoppeViewerClaimDownloadForm = reactCreateMemo(
    function DoppeViewerClaimDownloadForm(props: DoppeViewerClaimDownloadForm.Props) {
        const {actionId, pageId, onDownloadStarted, onCancel} = props;

        const viewerLocales = useDoppeViewerLocales();
        const clientServices = useDoppeViewerClientServices();
        const bymoPageProps = useBymoPageProps();

        const mediaFileId = useMemo(() => {
            const action = bymoPagePropsFindActionById(bymoPageProps, actionId);
            if (!action) {
                return null;
            }
            return doppeActionResolveBoundDownloadFileId(action);
        }, [bymoPageProps, actionId]);

        const [serverErrorMessage, setServerErrorMessage] = useState<string | null>(null);
        const [submitOperation, setSubmitOperation] = useAsyncOperation<void>();
        const isSubmitting = submitOperation?.state === AsyncValueStates.Pending;

        const requiredFieldValidator = useCallback((value: string) => {
            return value.trim().length === 0 ? viewerLocales['requiredField'] : undefined;
        }, [viewerLocales]);

        const emailFormatValidator = useMemo(
            () => doppeViewerClaimDownloadFormCreateEmailFormatValidator(
                viewerLocales['claimDownload.dialog.errorEmailInvalid'],
            ),
            [viewerLocales],
        );

        const initialFormData = useMemo<ClaimDownloadFormData>(() => ({
            orderNumber: '',
            email: '',
        }), []);

        const formFields = useMemo<DoppeViewerAutoForm.FormField<ClaimDownloadFormData>[]>(() => [
            {
                fieldName: 'orderNumber',
                title: viewerLocales['claimDownload.dialog.orderNumberLabel'],
                control: doppeViewerTextBoxControlCreate({}),
                mandatory: true,
                validators: [requiredFieldValidator],
            },
            {
                fieldName: 'email',
                title: viewerLocales['claimDownload.dialog.emailLabel'],
                control: doppeViewerTextBoxControlCreate({type: TextBoxTypes.Email}),
                mandatory: true,
                validators: [requiredFieldValidator, emailFormatValidator],
            },
        ], [viewerLocales, requiredFieldValidator, emailFormatValidator]);

        const handleSubmit = useEventCallback((formData: ClaimDownloadFormData) => {
            if (!mediaFileId) {
                setServerErrorMessage(viewerLocales['claimDownload.dialog.errorGeneric']);
                return;
            }

            setServerErrorMessage(null);

            void setSubmitOperation((async () => {
                try {
                    const result = await clientServices.getApiClient().generatePaidDownloadFileUrl({
                        pageId: pageId,
                        actionId: actionId,
                        mediaFileId: mediaFileId,
                        orderNumber: formData.orderNumber.trim(),
                        email: formData.email.trim(),
                    });

                    if (result.status === DoppeWixCheckoutOrderMatchStatus.Matched) {
                        onDownloadStarted?.();
                        window.location.href = result.downloadUrlInfo.url;
                        return;
                    }

                    setServerErrorMessage(viewerLocales['claimDownload.dialog.errorOrderNotFound']);
                } catch {
                    setServerErrorMessage(viewerLocales['claimDownload.dialog.errorGeneric']);
                }
            })());
        });

        return (
            <DoppeViewerAutoForm
                className={style(classes.root, {})}
                initialFormData={initialFormData}
                formFields={formFields}
                onSubmit={handleSubmit}
            >
                {(renderProps) => (
                    <>
                        <p className={classes.subtitle}>
                            {viewerLocales['claimDownload.dialog.subtitle']}
                        </p>
                        <div className={classes.fields}>
                            {renderProps.formFields}
                        </div>
                        {valueConvertWhenNotNullable(serverErrorMessage, message => (
                            <p className={classes.error}>{message}</p>
                        ))}
                        <div className={style(classes.actions, {singleAction: !onCancel})}>
                            {valueConvertWhenNotNullable(onCancel, cancelCallback => (
                                <DoppeViewerHorizontalButton
                                    buttonType={ButtonType.NormalButton}
                                    priority={DoppeViewerHorizontalButtonPriority.Secondary}
                                    skin={DoppeViewerHorizontalButtonSkin.Default}
                                    onClick={cancelCallback}
                                    disabled={isSubmitting}
                                >
                                    {viewerLocales['claimDownload.dialog.closeButton']}
                                </DoppeViewerHorizontalButton>
                            ))}
                            <DoppeViewerHorizontalButton
                                className={style(classes.downloadButton, {})}
                                buttonType={ButtonType.SubmitButton}
                                priority={DoppeViewerHorizontalButtonPriority.Primary}
                                skin={DoppeViewerHorizontalButtonSkin.Default}
                                isLoading={isSubmitting}
                                disabled={isSubmitting}
                            >
                                {viewerLocales['claimDownload.dialog.downloadButton']}
                            </DoppeViewerHorizontalButton>
                        </div>
                    </>
                )}
            </DoppeViewerAutoForm>
        );
    }
);

export namespace DoppeViewerClaimDownloadForm {
    export interface Props {
        actionId: string;
        pageId: string;
        /**
         * Called right before navigating to the signed download URL. Lets the
         * host modal close itself before the page unloads.
         */
        onDownloadStarted?: () => void;
        /**
         * If provided, renders a secondary "Close" button next to the primary
         * Download button. Omit when the host modal already provides a close
         * affordance (e.g., the new Buy/Claim modal uses the modal header X).
         */
        onCancel?: () => void;
    }
}
